<template>
  <div>
    <!-- hiddenDiv used to set button color of rainforest component -->
    <div
      class="text-primary-600"
      ref="hiddenDiv"
    ></div>
    <div class="relative">
      <!-- Rainforest web component dynamically created to avoid Vue 3 compatibility issues -->
      <div ref="rfPaymentContainer"></div>

      <div
        class="flex items-center justify-center absolute top-0 left-0 w-full h-full bg-opacity-60 bg-gray-900 z-10"
        v-if="loading"
        @click.stop.prevent
      >
        <client-loading-indicator styled />
      </div>
    </div>
  </div>
</template>

<script>
import Payments from "../../repository/PortalPayments"
import ClientLoadingIndicator from "../../client_auth/components/client-loading-indicator.vue"

export default {
  name: "PortalRainforestPaymentMethodForm",
  components: { ClientLoadingIndicator },
  emits: ["loaded", "approve", "attempted", "valid", "invalid"],
  inheritAttrs: false,

  props: {
    clientId: { required: false, type: Number },
    allowedMethods: { required: false, default: "CARD,ACH" },
    intent: { required: true, type: Object },
    loadMethodAfterApprove: { required: false, type: Boolean },
  },

  beforeUnmount() {
    this.destroyRainforestElement()
  },

  data() {
    return {
      attempts: 0,
      loading: false,
      rfElement: null,
    }
  },

  mounted() {
    this.$nextTick(() => {
      this.createRainforestElement()
    })
  },

  watch: {
    clientId() {
      this.createIntent()
    },
    intent: {
      handler() {
        // Recreate element when intent changes
        this.$nextTick(() => {
          this.createRainforestElement()
        })
      },
      deep: true,
    },
  },

  computed: {
    //using hiddenDiv to grab the color off of it to pass as prop to rainforest component
    primaryColor() {
      if (!this.$refs.hiddenDiv) {
        return null
      } // Add a guard clause
      const computedStyle = window.getComputedStyle(this.$refs.hiddenDiv)
      return computedStyle.color
    },
  },

  methods: {
    createRainforestElement() {
      const container = this.$refs.rfPaymentContainer
      if (!container) return

      // Clean up any existing element first
      this.destroyRainforestElement()

      const rfPaymentElement = document.createElement("rainforest-payment")
      rfPaymentElement.setAttribute("session-key", this.intent.session_key)
      rfPaymentElement.setAttribute("payment-method-config-id", this.intent.payment_method_config_id)
      rfPaymentElement.setAttribute("allowed-methods", this.allowedMethods)

      // Set button color from computed primaryColor
      const buttonColor = this.primaryColor
      if (buttonColor) {
        rfPaymentElement.setAttribute("style-button-color", buttonColor)
      }

      // Apply any additional attrs passed via v-bind="$attrs"
      Object.keys(this.$attrs).forEach((key) => {
        const attrName = key.replace(/([A-Z])/g, "-$1").toLowerCase()
        rfPaymentElement.setAttribute(attrName, this.$attrs[key])
      })

      // Add event listeners
      rfPaymentElement.addEventListener("approved", this.approved)
      rfPaymentElement.addEventListener("loaded", this.loaded)
      rfPaymentElement.addEventListener("attempted", this.attempted)
      rfPaymentElement.addEventListener("valid", this.valid)
      rfPaymentElement.addEventListener("invalid", this.invalid)

      container.appendChild(rfPaymentElement)
      this.rfElement = rfPaymentElement
    },

    destroyRainforestElement() {
      if (this.rfElement) {
        this.rfElement.removeEventListener("approved", this.approved)
        this.rfElement.removeEventListener("loaded", this.loaded)
        this.rfElement.removeEventListener("attempted", this.attempted)
        this.rfElement.removeEventListener("valid", this.valid)
        this.rfElement.removeEventListener("invalid", this.invalid)
        this.rfElement.remove()
        this.rfElement = null
      }
    },

    loaded() {
      this.$emit("loaded")
    },

    attempted() {
      this.$emit("attempted")
    },

    valid() {
      this.$emit("valid")
    },

    invalid() {
      this.$emit("invalid")
    },

    approved(data) {
      //if we are providing clientId we are adding payment method from portal
      if (this.clientId) {
        setTimeout(() => this.loadMethod(data), 500)
      } else {
        //if no clientId we are providing a payment method in the proposal flow
        this.$emit("approve")
      }
    },

    async loadMethod(data) {
      this.loading = true
      try {
        const { data } = await Payments.show(this.clientId, this.intent.payment_method_config_id)
        this.$emit("approve", data.data)
        this.loading = false
      } catch (e) {
        if (e.response?.status === 404 && this.attempts < 10) {
          this.attempts++
          setTimeout(() => {
            this.loadMethod(data)
          }, 500)
        } else {
          this.$alert("Unable to add payment method. Please try again.")
          this.loading = false
        }
      }
    },
  },
}
</script>

<style scoped></style>
